import type { ReactNode } from 'react';
import { cn } from 'helpers/cn';
import { Textarea } from '@/components/ui/textarea';

/**
 * The form controls the report is built from, shared by every screen that asks a question.
 * Pill chips rather than cards: a question is a handful of short words, and all of them
 * have to fit on screen at once for the review layout to read as a single page.
 */

type QuestionProps = { title: string; children: ReactNode };

export const Question = ({ title, children }: QuestionProps) => (
  <div className="flex flex-col gap-2">
    <h3 className="micro-label text-foreground">{title}</h3>
    {children}
  </div>
);

type ChipProps = {
  selected: boolean;
  onSelect: () => void;
  role: 'radio' | 'checkbox';
  children: ReactNode;
  /** A small mono tag after the label, e.g. "suggested". */
  tag?: string;
};

export const Chip = ({ selected, onSelect, role, children, tag }: ChipProps) => (
  <button
    type="button"
    role={role}
    aria-checked={selected}
    onClick={onSelect}
    className={cn(
      'rounded-pill flex h-[30px] items-center gap-1.5 border px-3 text-meta transition-colors duration-150',
      'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-hidden',
      selected ? 'border-sel-line bg-sel-bg text-foreground' : 'border-input bg-panel text-foreground hover:bg-hover',
    )}
  >
    {children}
    {tag && <span className="text-sel font-mono text-[9px] tracking-[0.1em] uppercase">{tag}</span>}
  </button>
);

type SingleChoiceProps<T extends string> = {
  label: string;
  choices: readonly { value: T; label: string }[];
  value: T | null;
  onChange: (value: T | null) => void;
  /** The choice to mark as Pertento's suggestion. */
  suggested?: T | null;
};

/** One answer at most. Tapping the chosen chip again clears it, since every question is optional. */
export const SingleChoice = <T extends string>({
  label,
  choices,
  value,
  onChange,
  suggested,
}: SingleChoiceProps<T>) => (
  <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
    {choices.map((choice) => (
      <Chip
        key={choice.value}
        role="radio"
        selected={value === choice.value}
        onSelect={() => onChange(value === choice.value ? null : choice.value)}
        tag={suggested === choice.value ? 'suggested' : undefined}
      >
        {choice.label}
      </Chip>
    ))}
  </div>
);

type MultiChoiceProps<T extends string> = {
  label: string;
  choices: readonly { value: T; label: string }[];
  values: readonly T[];
  onToggle: (value: T) => void;
};

export const MultiChoice = <T extends string>({ label, choices, values, onToggle }: MultiChoiceProps<T>) => (
  <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
    {choices.map((choice) => (
      <Chip
        key={choice.value}
        role="checkbox"
        selected={values.includes(choice.value)}
        onSelect={() => onToggle(choice.value)}
      >
        {choice.label}
      </Chip>
    ))}
  </div>
);

type TextAnswerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  limit: number;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
};

export const TextAnswer = ({ label, value, onChange, limit, placeholder, rows = 3, autoFocus }: TextAnswerProps) => (
  <div className="flex flex-col gap-1">
    <Textarea
      aria-label={label}
      value={value}
      rows={rows}
      maxLength={limit}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(ev) => onChange(ev.target.value)}
      className="border-line bg-field text-foreground rounded-lg text-body"
    />
    <span className="num text-dim self-end text-micro">
      {value.length} / {limit}
    </span>
  </div>
);
